export const DOCUMENT_KIND = "org.freefactor.echarts-config";
export const SCHEMA_VERSION = 2;
export const ECHARTS_VERSION = "6.1.0";
export const APP_VERSION = "2.0.0-sql.20260716";
export const SQL_ENGINE_VERSION = "1.14.1";
export const GENERATOR_VERSION = "mulberry32-wave-v1";
export const MAX_SERIES = 8;
export const MAX_POINTS = 5_000;

const HOUR_MS = 60 * 60 * 1_000;
const DAY_MS = 24 * HOUR_MS;
const MAX_EPOCH_MS = 8_640_000_000_000_000;
const DEFAULT_INTERVAL_MS = 30 * 60 * 1_000;
const SERIES_TYPES = new Set(["line", "area", "step", "bar", "scatter"]);
const LEGEND_POSITIONS = new Set(["top", "bottom", "left", "right"]);
const TOOLTIP_TRIGGERS = new Set(["axis", "item"]);
const STEP_POSITIONS = new Set(["start", "middle", "end"]);
const SYMBOLS = new Set([
  "none",
  "circle",
  "rect",
  "roundRect",
  "triangle",
  "diamond",
  "pin",
  "arrow",
]);
const COLOR_PATTERN = /^#(?:[\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i;
const SERIES_ID_PATTERN = /^[A-Za-z][A-Za-z\d_-]{0,63}$/;

export const DEFAULT_SQL_QUERY = `SELECT
  CAST((timestamp - $start_ms) / 3600000 AS INTEGER) * 3600000 + $start_ms AS timestamp,
  series_id,
  ROUND(AVG(value), 3) AS value
FROM readings
WHERE timestamp BETWEEN $start_ms AND $end_ms
GROUP BY 1, series_id
HAVING COUNT(value) > 0
ORDER BY 1, series_id
LIMIT 50000`;

const DEFAULT_SERIES = [
  {
    id: "signal-a",
    name: "Signal A",
    type: "line",
    color: "#5470c6",
    visible: true,
    lineWidth: 3,
    smooth: true,
    areaOpacity: 0.24,
    step: "middle",
    symbol: "none",
    symbolSize: 7,
    generator: {
      baseline: 52,
      amplitude: 17,
      periodMs: 12 * HOUR_MS,
      phase: 0,
      trendPerHour: 0.08,
      noise: 2.8,
      seedOffset: 11,
    },
  },
  {
    id: "signal-b",
    name: "Signal B",
    type: "line",
    color: "#91cc75",
    visible: true,
    lineWidth: 2,
    smooth: false,
    areaOpacity: 0.18,
    step: "middle",
    symbol: "none",
    symbolSize: 7,
    generator: {
      baseline: 38,
      amplitude: 11,
      periodMs: 8 * HOUR_MS,
      phase: 0.25,
      trendPerHour: -0.03,
      noise: 2.2,
      seedOffset: 29,
    },
  },
];

function isPlainObject(value) {
  if (value === null || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function scalarOr(value, fallback) {
  if (value === undefined) return fallback;
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  return null;
}

function numberOr(value, fallback) {
  if (value === undefined) return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }
  return scalarOr(value, fallback);
}

function integerOr(value, fallback) {
  const number = numberOr(value, fallback);
  return typeof number === "number" && Number.isFinite(number)
    ? Math.trunc(number)
    : number;
}

function booleanOr(value, fallback) {
  return value === undefined ? fallback : scalarOr(value, fallback);
}

function stringOr(value, fallback) {
  return value === undefined ? fallback : scalarOr(value, fallback);
}

function nullableNumberOr(value, fallback) {
  if (value === "") return null;
  return numberOr(value, fallback);
}

function createDefaults(nowMs) {
  if (!Number.isFinite(nowMs) || Math.abs(nowMs) > MAX_EPOCH_MS) {
    throw new TypeError("nowMs must be a finite JavaScript epoch value");
  }

  const endMs = Math.floor(nowMs / DEFAULT_INTERVAL_MS) * DEFAULT_INTERVAL_MS;

  return {
    title: "Generated telemetry",
    subtitle: "Deterministic sample data",
    backgroundColor: "#ffffff",
    seed: 20_260_714,
    time: {
      startMs: endMs - DAY_MS,
      endMs,
      intervalMs: DEFAULT_INTERVAL_MS,
    },
    yAxis: {
      min: null,
      max: null,
      label: "Value",
      scale: false,
    },
    legend: {
      show: true,
      position: "top",
    },
    tooltip: {
      show: true,
      trigger: "axis",
    },
    grid: {
      show: true,
      left: 58,
      right: 28,
      top: 82,
      bottom: 74,
    },
    animation: {
      enabled: true,
      duration: 500,
    },
    dataZoom: {
      show: true,
      start: 0,
      end: 100,
    },
    sql: {
      enabled: true,
      query: DEFAULT_SQL_QUERY,
    },
    series: cloneJson(DEFAULT_SERIES),
  };
}

/**
 * Create a complete, JSON-only editor configuration.
 * Passing nowMs makes the result deterministic for tests and external callers.
 */
export function createDefaultConfig(nowMs = Date.now()) {
  return createDefaults(nowMs);
}

function normalizeSeries(series, fallback, index) {
  const value = isPlainObject(series) ? series : {};
  const defaultSeries = fallback ?? {
    ...DEFAULT_SERIES[0],
    id: `series-${index + 1}`,
    name: `Series ${index + 1}`,
    color: [
      "#5470c6",
      "#91cc75",
      "#fac858",
      "#ee6666",
      "#73c0de",
      "#3ba272",
      "#fc8452",
      "#9a60b4",
    ][index % MAX_SERIES],
    generator: {
      ...DEFAULT_SERIES[0].generator,
      seedOffset: index * 101,
      phase: index / 8,
    },
  };
  const generator = isPlainObject(value.generator) ? value.generator : {};

  return {
    id: stringOr(value.id, defaultSeries.id),
    name: stringOr(value.name, defaultSeries.name),
    type: stringOr(value.type, defaultSeries.type),
    color: stringOr(value.color, defaultSeries.color),
    visible: booleanOr(value.visible, defaultSeries.visible),
    lineWidth: numberOr(value.lineWidth, defaultSeries.lineWidth),
    smooth: booleanOr(value.smooth, defaultSeries.smooth),
    areaOpacity: numberOr(value.areaOpacity, defaultSeries.areaOpacity),
    step: stringOr(value.step, defaultSeries.step),
    symbol: stringOr(value.symbol, defaultSeries.symbol),
    symbolSize: numberOr(value.symbolSize, defaultSeries.symbolSize),
    generator: {
      baseline: numberOr(generator.baseline, defaultSeries.generator.baseline),
      amplitude: numberOr(generator.amplitude, defaultSeries.generator.amplitude),
      periodMs: integerOr(generator.periodMs, defaultSeries.generator.periodMs),
      phase: numberOr(generator.phase, defaultSeries.generator.phase),
      trendPerHour: numberOr(
        generator.trendPerHour,
        defaultSeries.generator.trendPerHour,
      ),
      noise: numberOr(generator.noise, defaultSeries.generator.noise),
      seedOffset: integerOr(
        generator.seedOffset,
        defaultSeries.generator.seedOffset,
      ),
    },
  };
}

/**
 * Merge a partial configuration with defaults and coerce numeric form values.
 * Unknown properties are intentionally omitted from the persisted schema.
 */
export function normalizeConfig(config = {}, nowMs) {
  const source = isPlainObject(config) ? config : {};
  const sourceTime = isPlainObject(source.time) ? source.time : {};
  const inferredNow =
    nowMs ??
    (typeof sourceTime.endMs === "number" && Number.isFinite(sourceTime.endMs)
      ? sourceTime.endMs
      : Date.now());
  const defaults = createDefaults(inferredNow);
  const yAxis = isPlainObject(source.yAxis) ? source.yAxis : {};
  const legend = isPlainObject(source.legend) ? source.legend : {};
  const tooltip = isPlainObject(source.tooltip) ? source.tooltip : {};
  const grid = isPlainObject(source.grid) ? source.grid : {};
  const animation = isPlainObject(source.animation) ? source.animation : {};
  const dataZoom = isPlainObject(source.dataZoom) ? source.dataZoom : {};
  const sql = isPlainObject(source.sql) ? source.sql : {};
  const series =
    source.series === undefined
      ? defaults.series
      : Array.isArray(source.series)
        ? source.series.map((item, index) =>
            normalizeSeries(item, defaults.series[index], index),
          )
        : [];

  return {
    title: stringOr(source.title, defaults.title),
    subtitle: stringOr(source.subtitle, defaults.subtitle),
    backgroundColor: stringOr(
      source.backgroundColor,
      defaults.backgroundColor,
    ),
    seed: integerOr(source.seed, defaults.seed),
    time: {
      startMs: integerOr(sourceTime.startMs, defaults.time.startMs),
      endMs: integerOr(sourceTime.endMs, defaults.time.endMs),
      intervalMs: integerOr(
        sourceTime.intervalMs,
        defaults.time.intervalMs,
      ),
    },
    yAxis: {
      min: nullableNumberOr(yAxis.min, defaults.yAxis.min),
      max: nullableNumberOr(yAxis.max, defaults.yAxis.max),
      label: stringOr(yAxis.label, defaults.yAxis.label),
      scale: booleanOr(yAxis.scale, defaults.yAxis.scale),
    },
    legend: {
      show: booleanOr(legend.show, defaults.legend.show),
      position: stringOr(legend.position, defaults.legend.position),
    },
    tooltip: {
      show: booleanOr(tooltip.show, defaults.tooltip.show),
      trigger: stringOr(tooltip.trigger, defaults.tooltip.trigger),
    },
    grid: {
      show: booleanOr(grid.show, defaults.grid.show),
      left: numberOr(grid.left, defaults.grid.left),
      right: numberOr(grid.right, defaults.grid.right),
      top: numberOr(grid.top, defaults.grid.top),
      bottom: numberOr(grid.bottom, defaults.grid.bottom),
    },
    animation: {
      enabled: booleanOr(animation.enabled, defaults.animation.enabled),
      duration: integerOr(animation.duration, defaults.animation.duration),
    },
    dataZoom: {
      show: booleanOr(dataZoom.show, defaults.dataZoom.show),
      start: numberOr(dataZoom.start, defaults.dataZoom.start),
      end: numberOr(dataZoom.end, defaults.dataZoom.end),
    },
    sql: {
      enabled: booleanOr(sql.enabled, defaults.sql.enabled),
      query: stringOr(sql.query, defaults.sql.query),
    },
    series,
  };
}

function inspectJsonSafety(value, path, errors, seen) {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) errors.push(`${path} must be a finite number`);
    return;
  }
  if (typeof value !== "object") {
    errors.push(`${path} contains a non-JSON ${typeof value} value`);
    return;
  }
  if (seen.has(value)) {
    errors.push(`${path} contains a circular reference`);
    return;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      inspectJsonSafety(item, `${path}[${index}]`, errors, seen),
    );
  } else if (!isPlainObject(value)) {
    errors.push(`${path} must be a plain JSON object`);
  } else {
    for (const [key, item] of Object.entries(value)) {
      inspectJsonSafety(item, `${path}.${key}`, errors, seen);
    }
  }
  seen.delete(value);
}

function expectObject(value, path, errors) {
  if (!isPlainObject(value)) {
    errors.push(`${path} must be an object`);
    return false;
  }
  return true;
}

function expectString(value, path, errors, { min = 0, max = 120 } = {}) {
  if (typeof value !== "string" || value.length < min || value.length > max) {
    errors.push(`${path} must be a string between ${min} and ${max} characters`);
    return false;
  }
  return true;
}

function expectBoolean(value, path, errors) {
  if (typeof value !== "boolean") {
    errors.push(`${path} must be a boolean`);
    return false;
  }
  return true;
}

function expectFinite(
  value,
  path,
  errors,
  { min = -Number.MAX_VALUE, max = Number.MAX_VALUE, integer = false } = {},
) {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < min ||
    value > max ||
    (integer && !Number.isSafeInteger(value))
  ) {
    const type = integer ? "integer" : "number";
    errors.push(`${path} must be a finite ${type} between ${min} and ${max}`);
    return false;
  }
  return true;
}

function expectEnum(value, allowed, path, errors) {
  if (!allowed.has(value)) {
    errors.push(`${path} must be one of: ${[...allowed].join(", ")}`);
    return false;
  }
  return true;
}

function expectColor(value, path, errors) {
  if (typeof value !== "string" || !COLOR_PATTERN.test(value)) {
    errors.push(`${path} must be a hex color`);
    return false;
  }
  return true;
}

/** Validate the canonical configuration without mutating it. */
export function validateConfig(config) {
  const errors = [];
  inspectJsonSafety(config, "config", errors, new WeakSet());
  if (!expectObject(config, "config", errors)) return { valid: false, errors };

  expectString(config.title, "config.title", errors, { max: 120 });
  expectString(config.subtitle, "config.subtitle", errors, { max: 180 });
  expectColor(config.backgroundColor, "config.backgroundColor", errors);
  expectFinite(config.seed, "config.seed", errors, {
    min: -2_147_483_648,
    max: 2_147_483_647,
    integer: true,
  });

  if (expectObject(config.time, "config.time", errors)) {
    const startIsValid = expectFinite(
      config.time.startMs,
      "config.time.startMs",
      errors,
      { min: -MAX_EPOCH_MS, max: MAX_EPOCH_MS, integer: true },
    );
    const endIsValid = expectFinite(
      config.time.endMs,
      "config.time.endMs",
      errors,
      { min: -MAX_EPOCH_MS, max: MAX_EPOCH_MS, integer: true },
    );
    const intervalIsValid = expectFinite(
      config.time.intervalMs,
      "config.time.intervalMs",
      errors,
      { min: 1_000, max: DAY_MS, integer: true },
    );
    if (startIsValid && endIsValid && config.time.startMs >= config.time.endMs) {
      errors.push("config.time.startMs must be before config.time.endMs");
    }
    if (startIsValid && endIsValid && intervalIsValid) {
      const pointCount =
        Math.floor(
          (config.time.endMs - config.time.startMs) / config.time.intervalMs,
        ) + 1;
      if (pointCount > MAX_POINTS) {
        errors.push(`config.time produces more than ${MAX_POINTS} points`);
      }
    }
  }

  if (expectObject(config.yAxis, "config.yAxis", errors)) {
    const minIsValid =
      config.yAxis.min === null ||
      expectFinite(config.yAxis.min, "config.yAxis.min", errors);
    const maxIsValid =
      config.yAxis.max === null ||
      expectFinite(config.yAxis.max, "config.yAxis.max", errors);
    expectString(config.yAxis.label, "config.yAxis.label", errors, { max: 80 });
    expectBoolean(config.yAxis.scale, "config.yAxis.scale", errors);
    if (
      minIsValid &&
      maxIsValid &&
      config.yAxis.min !== null &&
      config.yAxis.max !== null &&
      config.yAxis.min >= config.yAxis.max
    ) {
      errors.push("config.yAxis.min must be less than config.yAxis.max");
    }
  }

  if (expectObject(config.legend, "config.legend", errors)) {
    expectBoolean(config.legend.show, "config.legend.show", errors);
    expectEnum(
      config.legend.position,
      LEGEND_POSITIONS,
      "config.legend.position",
      errors,
    );
  }

  if (expectObject(config.tooltip, "config.tooltip", errors)) {
    expectBoolean(config.tooltip.show, "config.tooltip.show", errors);
    expectEnum(
      config.tooltip.trigger,
      TOOLTIP_TRIGGERS,
      "config.tooltip.trigger",
      errors,
    );
  }

  if (expectObject(config.grid, "config.grid", errors)) {
    expectBoolean(config.grid.show, "config.grid.show", errors);
    for (const key of ["left", "right", "top", "bottom"]) {
      expectFinite(config.grid[key], `config.grid.${key}`, errors, {
        min: 0,
        max: 400,
      });
    }
  }

  if (expectObject(config.animation, "config.animation", errors)) {
    expectBoolean(config.animation.enabled, "config.animation.enabled", errors);
    expectFinite(config.animation.duration, "config.animation.duration", errors, {
      min: 0,
      max: 10_000,
      integer: true,
    });
  }

  if (expectObject(config.dataZoom, "config.dataZoom", errors)) {
    expectBoolean(config.dataZoom.show, "config.dataZoom.show", errors);
    const startIsValid = expectFinite(
      config.dataZoom.start,
      "config.dataZoom.start",
      errors,
      { min: 0, max: 100 },
    );
    const endIsValid = expectFinite(
      config.dataZoom.end,
      "config.dataZoom.end",
      errors,
      { min: 0, max: 100 },
    );
    if (startIsValid && endIsValid && config.dataZoom.start >= config.dataZoom.end) {
      errors.push("config.dataZoom.start must be less than config.dataZoom.end");
    }
  }

  if (expectObject(config.sql, "config.sql", errors)) {
    expectBoolean(config.sql.enabled, "config.sql.enabled", errors);
    expectString(config.sql.query, "config.sql.query", errors, {
      min: 1,
      max: 8000,
    });
    if (
      typeof config.sql.query === "string" &&
      !/^(SELECT|WITH)\b/i.test(config.sql.query.trim())
    ) {
      errors.push("config.sql.query must be a read-only SELECT or WITH query");
    }
  }

  if (!Array.isArray(config.series)) {
    errors.push("config.series must be an array");
  } else {
    if (config.series.length < 1 || config.series.length > MAX_SERIES) {
      errors.push(`config.series must contain between 1 and ${MAX_SERIES} series`);
    }
    const ids = new Set();
    config.series.forEach((series, index) => {
      const path = `config.series[${index}]`;
      if (!expectObject(series, path, errors)) return;
      if (
        !expectString(series.id, `${path}.id`, errors, { min: 1, max: 64 }) ||
        !SERIES_ID_PATTERN.test(series.id)
      ) {
        errors.push(`${path}.id must be a stable alphanumeric identifier`);
      } else if (ids.has(series.id)) {
        errors.push(`${path}.id must be unique`);
      } else {
        ids.add(series.id);
      }
      expectString(series.name, `${path}.name`, errors, { min: 1, max: 80 });
      expectEnum(series.type, SERIES_TYPES, `${path}.type`, errors);
      expectColor(series.color, `${path}.color`, errors);
      expectBoolean(series.visible, `${path}.visible`, errors);
      expectFinite(series.lineWidth, `${path}.lineWidth`, errors, {
        min: 0.5,
        max: 12,
      });
      expectBoolean(series.smooth, `${path}.smooth`, errors);
      expectFinite(series.areaOpacity, `${path}.areaOpacity`, errors, {
        min: 0,
        max: 1,
      });
      expectEnum(series.step, STEP_POSITIONS, `${path}.step`, errors);
      expectEnum(series.symbol, SYMBOLS, `${path}.symbol`, errors);
      expectFinite(series.symbolSize, `${path}.symbolSize`, errors, {
        min: 1,
        max: 40,
      });

      if (expectObject(series.generator, `${path}.generator`, errors)) {
        expectFinite(
          series.generator.baseline,
          `${path}.generator.baseline`,
          errors,
          { min: -1_000_000, max: 1_000_000 },
        );
        expectFinite(
          series.generator.amplitude,
          `${path}.generator.amplitude`,
          errors,
          { min: 0, max: 1_000_000 },
        );
        expectFinite(
          series.generator.periodMs,
          `${path}.generator.periodMs`,
          errors,
          { min: 1_000, max: 365 * DAY_MS, integer: true },
        );
        expectFinite(series.generator.phase, `${path}.generator.phase`, errors, {
          min: -1_000,
          max: 1_000,
        });
        expectFinite(
          series.generator.trendPerHour,
          `${path}.generator.trendPerHour`,
          errors,
          { min: -100_000, max: 100_000 },
        );
        expectFinite(series.generator.noise, `${path}.generator.noise`, errors, {
          min: 0,
          max: 1_000_000,
        });
        expectFinite(
          series.generator.seedOffset,
          `${path}.generator.seedOffset`,
          errors,
          { min: -2_147_483_648, max: 2_147_483_647, integer: true },
        );
      }
    });
  }

  return { valid: errors.length === 0, errors };
}

function throwForErrors(context, errors) {
  const error = new TypeError(`${context}: ${errors.join("; ")}`);
  error.errors = [...errors];
  throw error;
}

function assertValidConfig(config) {
  const result = validateConfig(config);
  if (!result.valid) throwForErrors("Invalid chart configuration", result.errors);
}

function hashString(value) {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function roundValue(value) {
  const rounded = Math.round(value * 1_000) / 1_000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function buildSnapshot(config) {
  const { startMs, endMs, intervalMs } = config.time;
  const pointCount = Math.floor((endMs - startMs) / intervalMs) + 1;
  const randomizers = config.series.map((series) =>
    mulberry32(
      (config.seed ^ series.generator.seedOffset ^ hashString(series.id)) >>> 0,
    ),
  );
  const source = [];

  for (let pointIndex = 0; pointIndex < pointCount; pointIndex += 1) {
    const timestamp = startMs + pointIndex * intervalMs;
    const elapsedHours = (timestamp - startMs) / HOUR_MS;
    const row = [timestamp];

    config.series.forEach((series, seriesIndex) => {
      const generator = series.generator;
      const cycles =
        (timestamp - startMs) / generator.periodMs + generator.phase;
      const wave = Math.sin(2 * Math.PI * cycles) * generator.amplitude;
      const trend = generator.trendPerHour * elapsedHours;
      const noise = (randomizers[seriesIndex]() * 2 - 1) * generator.noise;
      row.push(roundValue(generator.baseline + wave + trend + noise));
    });
    source.push(row);
  }

  return {
    generator: GENERATOR_VERSION,
    startMs,
    endMs,
    intervalMs,
    dimensions: ["timestamp", ...config.series.map((series) => series.id)],
    source,
  };
}

function legendPlacement(position) {
  if (position === "bottom") return { left: "center", bottom: 30 };
  if (position === "left") return { left: 4, top: "middle", orient: "vertical" };
  if (position === "right") return { right: 4, top: "middle", orient: "vertical" };
  return { left: "center", top: 4 };
}

function buildSeriesOption(series) {
  const common = {
    id: series.id,
    name: series.name,
    type:
      series.type === "area" || series.type === "step"
        ? "line"
        : series.type,
    encode: { x: "timestamp", y: series.id },
    itemStyle: { color: series.color },
    emphasis: { focus: "series" },
  };

  if (series.type === "bar") {
    return { ...common, barMaxWidth: 42 };
  }

  if (series.type === "scatter") {
    return {
      ...common,
      symbol: series.symbol === "none" ? "circle" : series.symbol,
      symbolSize: series.symbolSize,
    };
  }

  return {
    ...common,
    smooth: series.type === "step" ? false : series.smooth,
    showSymbol: series.symbol !== "none",
    symbol: series.symbol,
    symbolSize: series.symbolSize,
    lineStyle: { width: series.lineWidth, color: series.color },
    ...(series.type === "area"
      ? { areaStyle: { opacity: series.areaOpacity, color: series.color } }
      : {}),
    ...(series.type === "step" ? { step: series.step } : {}),
  };
}

function buildEchartsOption(config, snapshot) {
  const visibleSeries = config.series.filter((series) => series.visible);
  const option = {
    useUTC: true,
    backgroundColor: config.backgroundColor,
    animation: config.animation.enabled,
    animationDuration: config.animation.duration,
    title: {
      text: config.title,
      subtext: config.subtitle,
      left: "center",
      top: 28,
    },
    color: visibleSeries.map((series) => series.color),
    legend: {
      show: config.legend.show,
      data: visibleSeries.map((series) => series.name),
      ...legendPlacement(config.legend.position),
    },
    tooltip: {
      show: config.tooltip.show,
      trigger: config.tooltip.trigger,
      axisPointer: { type: "cross" },
      confine: true,
    },
    grid: {
      show: config.grid.show,
      left: config.grid.left,
      right: config.grid.right,
      top: config.grid.top,
      bottom: config.grid.bottom,
      containLabel: true,
    },
    dataset: {
      dimensions: [...snapshot.dimensions],
      source: snapshot.source.map((row) => [...row]),
    },
    xAxis: {
      type: "time",
      min: config.time.startMs,
      max: config.time.endMs,
      boundaryGap: visibleSeries.some((series) => series.type === "bar"),
      splitLine: { show: config.grid.show },
    },
    yAxis: {
      type: "value",
      name: config.yAxis.label,
      scale: config.yAxis.scale,
      splitLine: { show: config.grid.show },
      ...(config.yAxis.min === null ? {} : { min: config.yAxis.min }),
      ...(config.yAxis.max === null ? {} : { max: config.yAxis.max }),
    },
    dataZoom: config.dataZoom.show
      ? [
          {
            type: "inside",
            start: config.dataZoom.start,
            end: config.dataZoom.end,
          },
          {
            type: "slider",
            start: config.dataZoom.start,
            end: config.dataZoom.end,
            height: 18,
            bottom: 8,
          },
        ]
      : [],
    series: visibleSeries.map(buildSeriesOption),
  };

  // This also makes accidental future undefined values visible during development.
  return cloneJson(option);
}

function jsonValuesEqual(left, right) {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => jsonValuesEqual(value, right[index]))
    );
  }
  if (!isPlainObject(left) || !isPlainObject(right)) return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) &&
        jsonValuesEqual(left[key], right[key]),
    )
  );
}

/** Generate deterministic sample data and its left-chart ECharts option. */
export function buildPreview(config) {
  const normalizedConfig = normalizeConfig(config);
  assertValidConfig(normalizedConfig);
  const snapshot = buildSnapshot(normalizedConfig);
  const option = buildEchartsOption(normalizedConfig, snapshot);
  return { config: normalizedConfig, snapshot, option };
}

function validateSourceSnapshot(snapshot, config, errors) {
  if (!expectObject(snapshot, "document.sourceSnapshot", errors)) return;
  if (snapshot.generator !== GENERATOR_VERSION) {
    errors.push(`document.sourceSnapshot.generator must be ${GENERATOR_VERSION}`);
  }
  for (const key of ["startMs", "endMs", "intervalMs"]) {
    expectFinite(snapshot[key], `document.sourceSnapshot.${key}`, errors, {
      integer: true,
    });
    if (snapshot[key] !== config.time[key]) {
      errors.push(`document.sourceSnapshot.${key} must match document.config.time.${key}`);
    }
  }

  const expectedDimensions = [
    "timestamp",
    ...config.series.map((series) => series.id),
  ];
  if (
    !Array.isArray(snapshot.dimensions) ||
    snapshot.dimensions.length !== expectedDimensions.length ||
    snapshot.dimensions.some(
      (dimension, index) => dimension !== expectedDimensions[index],
    )
  ) {
    errors.push("document.sourceSnapshot.dimensions must match the configured series IDs");
  }

  if (!Array.isArray(snapshot.source)) {
    errors.push("document.sourceSnapshot.source must be an array");
    return;
  }
  const expectedPointCount =
    Math.floor(
      (config.time.endMs - config.time.startMs) / config.time.intervalMs,
    ) + 1;
  if (snapshot.source.length !== expectedPointCount) {
    errors.push("document.sourceSnapshot.source has an unexpected point count");
  }
  if (snapshot.source.length > MAX_POINTS) {
    errors.push(`document.sourceSnapshot.source cannot exceed ${MAX_POINTS} rows`);
  }

  snapshot.source.forEach((row, rowIndex) => {
    const path = `document.sourceSnapshot.source[${rowIndex}]`;
    if (!Array.isArray(row) || row.length !== expectedDimensions.length) {
      errors.push(`${path} must contain one value per dimension`);
      return;
    }
    const expectedTimestamp = config.time.startMs + rowIndex * config.time.intervalMs;
    if (row[0] !== expectedTimestamp) {
      errors.push(`${path}[0] must be the expected monotonic timestamp`);
    }
    row.slice(1).forEach((value, valueIndex) => {
      if (value !== null && (typeof value !== "number" || !Number.isFinite(value))) {
        errors.push(`${path}[${valueIndex + 1}] must be finite or null`);
      }
    });
  });
}

function validateQuerySnapshot(snapshot, config, errors) {
  if (!expectObject(snapshot, "document.snapshot", errors)) return;
  if (snapshot.kind !== "sql-result-v1") {
    errors.push("document.snapshot.kind must be sql-result-v1");
  }
  const expectedDimensions = [
    "timestamp",
    ...config.series.map((series) => series.id),
  ];
  if (
    !Array.isArray(snapshot.dimensions) ||
    snapshot.dimensions.length !== expectedDimensions.length ||
    snapshot.dimensions.some(
      (dimension, index) => dimension !== expectedDimensions[index],
    )
  ) {
    errors.push("document.snapshot.dimensions must match the configured series IDs");
  }
  if (!Array.isArray(snapshot.source)) {
    errors.push("document.snapshot.source must be an array");
    return;
  }
  if (snapshot.source.length > MAX_POINTS) {
    errors.push(`document.snapshot.source cannot exceed ${MAX_POINTS} rows`);
  }

  let previousTimestamp = null;
  snapshot.source.forEach((row, rowIndex) => {
    const path = `document.snapshot.source[${rowIndex}]`;
    if (!Array.isArray(row) || row.length !== expectedDimensions.length) {
      errors.push(`${path} must contain one value per dimension`);
      return;
    }
    if (
      !Number.isSafeInteger(row[0]) ||
      Math.abs(row[0]) > MAX_EPOCH_MS
    ) {
      errors.push(`${path}[0] must be a safe JavaScript epoch integer`);
    }
    if (previousTimestamp !== null && row[0] <= previousTimestamp) {
      errors.push("document.snapshot timestamps must be strictly increasing");
    }
    previousTimestamp = row[0];
    row.slice(1).forEach((value, valueIndex) => {
      if (value !== null && (typeof value !== "number" || !Number.isFinite(value))) {
        errors.push(`${path}[${valueIndex + 1}] must be finite or null`);
      }
    });
  });
}

function validateQueryResult(queryResult, config, sourceSnapshot, snapshot, errors) {
  if (!expectObject(queryResult, "document.queryResult", errors)) return;
  expectBoolean(queryResult.enabled, "document.queryResult.enabled", errors);
  if (queryResult.enabled !== config.sql.enabled) {
    errors.push("document.queryResult.enabled must match document.config.sql.enabled");
  }
  if (config.sql.enabled && queryResult.engine !== "sql.js") {
    errors.push("document.queryResult.engine must be sql.js when SQL is enabled");
  }
  if (!config.sql.enabled && queryResult.engine !== null) {
    errors.push("document.queryResult.engine must be null when SQL is disabled");
  }
  if (queryResult.packageVersion !== SQL_ENGINE_VERSION) {
    errors.push(`document.queryResult.packageVersion must be ${SQL_ENGINE_VERSION}`);
  }
  if (
    config.sql.enabled &&
    (typeof queryResult.sqliteVersion !== "string" || !queryResult.sqliteVersion)
  ) {
    errors.push("document.queryResult.sqliteVersion must identify SQLite");
  }
  if (!config.sql.enabled && queryResult.sqliteVersion !== null) {
    errors.push("document.queryResult.sqliteVersion must be null when SQL is disabled");
  }
  for (const key of ["inputRows", "selectedRows", "outputPoints"]) {
    expectFinite(queryResult[key], `document.queryResult.${key}`, errors, {
      min: 0,
      max: 50000,
      integer: true,
    });
  }
  if (
    isPlainObject(sourceSnapshot) &&
    Array.isArray(sourceSnapshot.source) &&
    queryResult.inputRows !== sourceSnapshot.source.length * config.series.length
  ) {
    errors.push("document.queryResult.inputRows must match the long-form source row count");
  }
  if (
    isPlainObject(snapshot) &&
    Array.isArray(snapshot.source) &&
    queryResult.outputPoints !== snapshot.source.length
  ) {
    errors.push("document.queryResult.outputPoints must match document.snapshot.source");
  }
  if (queryResult.chartOrder !== "timestamp-ascending") {
    errors.push("document.queryResult.chartOrder must be timestamp-ascending");
  }
  if (expectObject(queryResult.parameters, "document.queryResult.parameters", errors)) {
    for (const [key, configKey] of [
      ["startMs", "startMs"],
      ["endMs", "endMs"],
      ["intervalMs", "intervalMs"],
    ]) {
      if (queryResult.parameters[key] !== config.time[configKey]) {
        errors.push(`document.queryResult.parameters.${key} must match document.config.time.${configKey}`);
      }
    }
  }
}

export function buildChartOption(config, snapshot) {
  const normalizedConfig = normalizeConfig(config);
  assertValidConfig(normalizedConfig);
  const errors = [];
  validateQuerySnapshot(snapshot, normalizedConfig, errors);
  if (errors.length) throwForErrors("Invalid SQL chart snapshot", errors);
  return buildEchartsOption(normalizedConfig, snapshot);
}

/** Validate a complete persisted document. */
export function validateDocument(document) {
  const errors = [];
  inspectJsonSafety(document, "document", errors, new WeakSet());
  if (!expectObject(document, "document", errors)) return { valid: false, errors };

  if (document.kind !== DOCUMENT_KIND) {
    errors.push(`document.kind must be ${DOCUMENT_KIND}`);
  }
  if (document.schemaVersion !== SCHEMA_VERSION) {
    errors.push(`document.schemaVersion must be ${SCHEMA_VERSION}`);
  }
  if (expectObject(document.engine, "document.engine", errors)) {
    if (document.engine.name !== "echarts") {
      errors.push("document.engine.name must be echarts");
    }
    if (document.engine.version !== ECHARTS_VERSION) {
      errors.push(`document.engine.version must be ${ECHARTS_VERSION}`);
    }
  }
  if (expectObject(document.app, "document.app", errors)) {
    if (document.app.name !== "freefactor-echarts-configurator") {
      errors.push("document.app.name must be freefactor-echarts-configurator");
    }
    if (document.app.version !== APP_VERSION) {
      errors.push(`document.app.version must be ${APP_VERSION}`);
    }
  }
  expectFinite(document.savedAtMs, "document.savedAtMs", errors, {
    min: 0,
    max: MAX_EPOCH_MS,
    integer: true,
  });

  const configResult = validateConfig(document.config);
  errors.push(
    ...configResult.errors.map((error) =>
      error.startsWith("config") ? `document.${error}` : error,
    ),
  );
  if (configResult.valid) {
    validateSourceSnapshot(document.sourceSnapshot, document.config, errors);
    validateQuerySnapshot(document.snapshot, document.config, errors);
    validateQueryResult(
      document.queryResult,
      document.config,
      document.sourceSnapshot,
      document.snapshot,
      errors,
    );
  }
  if (!isPlainObject(document.option)) {
    errors.push("document.option must be a JSON-safe ECharts option object");
  } else if (
    configResult.valid &&
    isPlainObject(document.snapshot) &&
    !errors.some((error) => error.startsWith("document.snapshot")) &&
    !jsonValuesEqual(
      document.option,
      buildEchartsOption(document.config, document.snapshot),
    )
  ) {
    errors.push("document.option must match document.config and document.snapshot");
  }

  return { valid: errors.length === 0, errors };
}

/** Create the portable, versioned Save payload. */
export function createSavedDocument(config, savedAtMs = Date.now(), preparedPreview = null) {
  if (
    !Number.isSafeInteger(savedAtMs) ||
    savedAtMs < 0 ||
    savedAtMs > MAX_EPOCH_MS
  ) {
    throw new TypeError("savedAtMs must be a non-negative JavaScript epoch integer");
  }
  const normalizedConfig = normalizeConfig(config);
  assertValidConfig(normalizedConfig);

  let sourceSnapshot;
  let snapshot;
  let queryResult;
  if (preparedPreview === null) {
    if (normalizedConfig.sql.enabled) {
      throw new TypeError("An executed SQL preview is required before saving");
    }
    const preview = buildPreview(normalizedConfig);
    sourceSnapshot = preview.snapshot;
    snapshot = {
      kind: "sql-result-v1",
      dimensions: [...sourceSnapshot.dimensions],
      source: sourceSnapshot.source.map((row) => [...row]),
    };
    queryResult = {
      enabled: false,
      engine: null,
      packageVersion: SQL_ENGINE_VERSION,
      sqliteVersion: null,
      inputRows: sourceSnapshot.source.length * normalizedConfig.series.length,
      selectedRows: sourceSnapshot.source.length * normalizedConfig.series.length,
      outputPoints: sourceSnapshot.source.length,
      chartOrder: "timestamp-ascending",
      parameters: {
        startMs: normalizedConfig.time.startMs,
        endMs: normalizedConfig.time.endMs,
        intervalMs: normalizedConfig.time.intervalMs,
      },
    };
  } else {
    if (!isPlainObject(preparedPreview)) {
      throw new TypeError("Prepared SQL preview must be an object");
    }
    if (!jsonValuesEqual(preparedPreview.config, normalizedConfig)) {
      throw new TypeError("Prepared SQL preview does not match the current configuration");
    }
    sourceSnapshot = cloneJson(preparedPreview.sourceSnapshot);
    snapshot = cloneJson(preparedPreview.snapshot);
    queryResult = cloneJson(preparedPreview.queryResult);
  }

  const option = buildChartOption(normalizedConfig, snapshot);
  const document = {
    kind: DOCUMENT_KIND,
    schemaVersion: SCHEMA_VERSION,
    engine: { name: "echarts", version: ECHARTS_VERSION },
    app: {
      name: "freefactor-echarts-configurator",
      version: APP_VERSION,
    },
    savedAtMs,
    config: cloneJson(normalizedConfig),
    sourceSnapshot,
    snapshot,
    queryResult,
    option,
  };
  const validation = validateDocument(document);
  if (!validation.valid) {
    throwForErrors("Invalid prepared chart document", validation.errors);
  }
  return document;
}

/** Serialize only documents that conform to schema version 2. */
export function serializeDocument(document) {
  const result = validateDocument(document);
  if (!result.valid) throwForErrors("Invalid saved chart document", result.errors);
  return JSON.stringify(document, null, 2);
}

/** Parse and validate untrusted serialized chart configuration. */
export function parseDocument(json) {
  if (typeof json !== "string") {
    throw new TypeError("Serialized chart document must be a string");
  }

  let document;
  try {
    document = JSON.parse(json);
  } catch (cause) {
    const error = new SyntaxError("Serialized chart document is not valid JSON");
    error.cause = cause;
    throw error;
  }

  const result = validateDocument(document);
  if (!result.valid) throwForErrors("Invalid saved chart document", result.errors);
  return document;
}

/**
 * Build the right-hand chart exclusively from a validated saved document.
 * The persisted option is informational; rebuilding prevents raw option injection.
 */
export function buildOptionFromDocument(document) {
  const result = validateDocument(document);
  if (!result.valid) throwForErrors("Invalid saved chart document", result.errors);
  return buildEchartsOption(document.config, document.snapshot);
}

export default createDefaultConfig;
